import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../../App.css';
import {
  BOTTLE_PRICE,
  createOrder,
  calculateTotal,
  formatCurrency,
  findOrder,
  LAUNCH_DATE,
  OUTSIDE_GAUTENG_SHIPPING,
} from '../../utils/orderStorage';
import { downloadOrderReceipt } from '../../utils/receiptGenerator';

const launchDateLabel = new Intl.DateTimeFormat('en-ZA', {
  dateStyle: 'long',
}).format(LAUNCH_DATE);

function Checkout() {
  const [searchParams] = useSearchParams();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [region, setRegion] = useState('gauteng');
  const [isAdult, setIsAdult] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [receiptError, setReceiptError] = useState('');

  const isOutsideGauteng = region === 'outside';

  const pricing = useMemo(
    () => calculateTotal(quantity, isOutsideGauteng),
    [quantity, isOutsideGauteng]
  );

  useEffect(() => {
    const paymentResult = searchParams.get('payment');
    const reference = searchParams.get('reference');

    if (paymentResult === 'cancelled') {
      setPaymentError('Payment was cancelled. Your card was not charged and you can try again.');
      return undefined;
    }
    if (paymentResult === 'failed') {
      setPaymentError('Yoco could not complete the payment. Please review your details and try again.');
      return undefined;
    }
    if (paymentResult !== 'success' || !reference) return undefined;

    const existingOrder = findOrder(reference);
    if (existingOrder) {
      setConfirmation(existingOrder);
      return undefined;
    }

    const draftValue = window.localStorage.getItem(`mukwei-yoco-draft:${reference}`);
    if (!draftValue) {
      setPaymentError(`Payment returned successfully, but order details for ${reference} were not found on this device. Please contact support with this reference.`);
      return undefined;
    }

    const draft = JSON.parse(draftValue);
    let attempts = 0;
    setIsVerifying(true);
    const verify = async () => {
      attempts += 1;
      try {
        const response = await fetch(
          `/api/payments/status/${encodeURIComponent(reference)}?accessToken=${encodeURIComponent(draft.receiptAccessToken || '')}`
        );
        const payment = await response.json();
        if (payment.status === 'succeeded') {
          const order = createOrder({
            ...draft,
            orderId: reference,
            paymentId: payment.paymentId,
            deliveryToken: payment.deliveryToken,
          });
          window.localStorage.removeItem(`mukwei-yoco-draft:${reference}`);
          setConfirmation(order);
          setIsVerifying(false);
          return;
        }
        if (payment.status === 'failed') {
          setPaymentError('Yoco reported that this payment failed. Please try again.');
          setIsVerifying(false);
          return;
        }
      } catch (error) {
        // A short network interruption is handled by the next poll.
      }
      if (attempts >= 15) {
        setPaymentError(`Yoco is still confirming payment ${reference}. Keep this reference and refresh shortly.`);
        setIsVerifying(false);
        return;
      }
      window.setTimeout(verify, 2000);
    };
    verify();
    return undefined;
  }, [searchParams]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPaymentError('');
    setIsRedirecting(true);
    try {
      const response = await fetch('/api/payments/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fullName, email, phone, address, quantity, region }),
      });
      const checkout = await response.json();
      if (!response.ok || !checkout.redirectUrl) {
        throw new Error(checkout.error || 'Yoco could not prepare the payment.');
      }
      window.localStorage.setItem(
        `mukwei-yoco-draft:${checkout.reference}`,
        JSON.stringify({
          fullName,
          email,
          phone,
          address,
          quantity,
          isOutsideGauteng,
          receiptAccessToken: checkout.receiptAccessToken,
        })
      );
      window.location.assign(checkout.redirectUrl);
    } catch (error) {
      setPaymentError(error.message);
      setIsRedirecting(false);
    }
  };

  return (
    <main className='content-page checkout-page'>
      <section className='checkout-shell'>
        <div className='checkout-product'>
          <picture>
            <source media='(max-width: 700px)' srcSet='/images/mukwei-product-720.webp' />
            <img
              src='/images/mukwei-product-1200.webp'
              alt='Mukwei Gin launch bottle and signature serve'
              decoding='async'
            />
          </picture>
          <div className='product-image-caption'>
            <p>Inaugural release</p>
            <strong>Mukwei Premium Gin</strong>
            <span>750ml · {formatCurrency(BOTTLE_PRICE)}</span>
          </div>
        </div>
        <div className='form-panel'>
          <p className='section-kicker'>Limited allocation</p>
          <h1>Reserve your bottle</h1>
          <p className='section-copy'>Complete your details below. Fulfilment begins {launchDateLabel}.</p>

        {paymentError && <div className='payment-message error' role='alert'>{paymentError}</div>}
        {isVerifying && (
          <div className='payment-message' role='status'>
            <span className='payment-spinner' aria-hidden='true' />
            Securely confirming your payment with Yoco…
          </div>
        )}

        {!confirmation && (
          <form className='checkout-form' onSubmit={handleSubmit}>
            <div className='field-row'>
              <div><label htmlFor='fullName'>Full name</label><input id='fullName' value={fullName} onChange={(event) => setFullName(event.target.value)} required /></div>
              <div><label htmlFor='phone'>Phone number</label><input id='phone' value={phone} onChange={(event) => setPhone(event.target.value)} required /></div>
            </div>
            <div><label htmlFor='email'>Email address</label><input id='email' type='email' value={email} onChange={(event) => setEmail(event.target.value)} required /></div>
            <div><label htmlFor='address'>Delivery address</label><textarea id='address' rows='3' value={address} onChange={(event) => setAddress(event.target.value)} required /></div>

            <div className='order-controls'>
              <div>
                <label id='quantity-label'>Quantity</label>
                <div className='quantity-stepper' aria-labelledby='quantity-label'>
                  <button type='button' onClick={() => setQuantity((value) => Math.max(1, value - 1))} disabled={quantity === 1} aria-label='Remove one bottle'>−</button>
                  <output aria-live='polite'>{quantity}</output>
                  <button type='button' onClick={() => setQuantity((value) => Math.min(12, value + 1))} disabled={quantity === 12} aria-label='Add one bottle'>+</button>
                </div>
              </div>
              <div>
                <label htmlFor='region'>Delivery region</label>
                <select id='region' value={region} onChange={(event) => setRegion(event.target.value)}>
                  <option value='gauteng'>In and around Gauteng</option>
                  <option value='outside'>Outside Gauteng</option>
                </select>
              </div>
            </div>

            <div className='price-box'>
              <p><span>{quantity} × Mukwei Gin</span><span>{formatCurrency(pricing.subtotal)}</span></p>
              <p><span>Delivery</span><span>{isOutsideGauteng ? formatCurrency(OUTSIDE_GAUTENG_SHIPPING) : 'Complimentary'}</span></p>
              <p className='total-line'><span>Total</span><span>{formatCurrency(pricing.total)}</span></p>
            </div>

            <label className='age-check'>
              <input type='checkbox' checked={isAdult} onChange={(event) => setIsAdult(event.target.checked)} required />
              <span>I confirm that I am 18 years or older. Alcohol is not for sale to persons under 18.</span>
            </label>
            <div className='payment-methods' aria-label='Yoco supported payment methods'>
              <span>Secure checkout by <strong>Yoco</strong></span>
              <div><span>VISA</span><span>Mastercard</span><span>Apple Pay</span><span>G Pay</span></div>
            </div>
            <p className='merchant-notice'>
              Your order is placed with Mukwei Gins (Pty) Ltd, the merchant responsible for
              payment, delivery and fulfilment. Phanda Studios provides hosting and web development
              services only.
            </p>
            <button type='submit' className='cta-button primary full-width' disabled={isRedirecting || isVerifying}>
              {isRedirecting ? 'Preparing secure payment…' : 'Pay securely with Yoco'} <span aria-hidden='true'>→</span>
            </button>
          </form>
        )}

        {confirmation && (
          <div className='confirmation-box'>
            <h2>Payment Successful</h2>
            <p>
              Thank you, {confirmation.fullName}. Your order reference is
              {' '}
              <strong>{confirmation.id}</strong>.
            </p>
            <p>
              Estimated delivery: {new Date(confirmation.estimatedDelivery).toLocaleDateString('en-ZA')}.
            </p>
            <p className='receipt-assurance'>
              Your personalised receipt includes a secure QR delivery pass registered to
              {' '}{confirmation.fullName}.
            </p>
            {receiptError && <p className='receipt-error' role='alert'>{receiptError}</p>}
            <div className='hero-actions'>
              <button
                type='button'
                className='cta-button primary'
                onClick={() => {
                  setReceiptError('');
                  downloadOrderReceipt(confirmation).catch((error) => setReceiptError(error.message));
                }}
              >
                Download receipt <span aria-hidden='true'>↓</span>
              </button>
              <Link to={`/track?orderId=${confirmation.id}`} className='cta-button primary'>
                Track This Order
              </Link>
              <Link to='/' className='cta-button secondary'>
                Back to Landing
              </Link>
            </div>
          </div>
        )}
        </div>
      </section>
    </main>
  );
}

export default Checkout;
